import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client, { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ArrowLeft, Check, Minus, Plus } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    client.get(`/products/${slug}`).then((r) => setP(r.data));
  }, [slug]);

  if (!p) return <div className="pt-40 text-center font-serif-display text-3xl">Brewing…</div>;

  const variant = p.variants?.[variantIdx];
  const price = variant?.price || p.price;
  const stock = variant?.stock ?? 100;
  const outOfStock = stock <= 0;
  const lowStock = !outOfStock && stock <= 10;

  const add = () => {
    if (outOfStock) return;
    if (qty > stock) {
      toast.error(`Only ${stock} left in stock`);
      return;
    }
    addItem({
      product_id: p.id,
      slug: p.slug,
      name: p.name,
      image: p.image,
      variant: variant?.label || "",
      price,
      quantity: qty,
    });
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div data-testid="product-page" className="pt-28 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#7B6F66] hover:text-[#C85A32] mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} /> Back to shop
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-7">
            <div className="aspect-[4/5] bg-[#F4EFE6] overflow-hidden">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            </div>
            {p.gallery?.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                {p.gallery.map((g, i) => (
                  <img key={i} src={g} alt="" className="aspect-square object-cover bg-[#F4EFE6]" />
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
              {p.category === "single-origin" ? "Single Origin" : p.category}
              {p.origin ? ` · ${p.origin}` : ""}
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl font-light leading-none">
              {p.name}
            </h1>
            <p className="mt-4 text-[#7B6F66] text-lg">{p.tagline}</p>

            <div className="mt-6 text-3xl font-serif-display text-[#1C3326]">{inr(price)}</div>
            <div data-testid="stock-indicator" className="mt-2 text-xs uppercase tracking-[0.25em]">
              {outOfStock ? (
                <span className="text-red-700">● Out of stock</span>
              ) : lowStock ? (
                <span className="text-[#C85A32]">● Only {stock} left</span>
              ) : (
                <span className="text-[#1C3326]">● In stock</span>
              )}
            </div>

            <p className="mt-8 leading-relaxed text-[#4A3B32]">{p.description}</p>

            {p.tasting_notes?.length > 0 && (
              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">
                  Tasting Notes
                </div>
                <div className="flex flex-wrap gap-2">
                  {p.tasting_notes.map((n) => (
                    <span key={n} className="border border-[#EBE3D5] px-3 py-1.5 text-xs uppercase tracking-[0.15em]">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {p.variants?.length > 0 && (
              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">
                  Size
                </div>
                <div className="flex flex-wrap gap-3">
                  {p.variants.map((v, i) => (
                    <button
                      key={v.label}
                      data-testid={`variant-${v.label}`}
                      onClick={() => setVariantIdx(i)}
                      className={`border px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${
                        variantIdx === i
                          ? "border-[#1C3326] bg-[#1C3326] text-[#FDFBF7]"
                          : "border-[#EBE3D5] hover:border-[#1C3326]"
                      }`}
                    >
                      {v.label} · {inr(v.price)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 flex items-stretch gap-4">
              <div className="flex items-center border border-[#EBE3D5]">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-4 hover:bg-[#F4EFE6]"
                  data-testid="qty-dec"
                >
                  <Minus size={14} />
                </button>
                <span className="px-4 text-sm w-8 text-center">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-4 hover:bg-[#F4EFE6]"
                  data-testid="qty-inc"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={add}
                disabled={outOfStock}
                data-testid="add-to-cart-button"
                className="flex-1 bg-[#1C3326] hover:bg-[#15261C] text-[#FDFBF7] py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {outOfStock ? "Sold out" : `Add to Cart · ${inr(price * qty)}`}
              </button>
            </div>

            <div className="mt-8 space-y-3 pt-8 border-t border-[#EBE3D5]">
              {[
                p.roast_level && `Roast: ${p.roast_level}`,
                p.process && `Process: ${p.process}`,
                "Free shipping on orders above ₹999",
                "Roasted fresh · shipped within 48 hours",
              ]
                .filter(Boolean)
                .map((f) => (
                  <div key={f} className="flex items-center gap-3 text-sm text-[#4A3B32]">
                    <Check size={14} className="text-[#C85A32]" /> {f}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
